import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getLanguagesByAppLanguage } from '../data/languages';
import { useLanguage } from '../contexts/LanguageContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChevronDown, FiSearch } = FiIcons;

const TranslationLanguageSelector = ({ value, onChange, includeAuto, label }) => {
  const { t, currentLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const languageNames = getLanguagesByAppLanguage(currentLanguage);

  // Create languages list with proper flags - removed Spanish variants
  const createLanguagesList = () => {
    const flags = {
      'auto': '🔍',
      'af': '🇿🇦',
      'sq': '🇦🇱',
      'am': '🇪🇹',
      'ar': '🇸🇦',
      'hy': '🇦🇲',
      'az': '🇦🇿',
      'eu': '🏴',
      'be': '🇧🇾',
      'bn': '🇧🇩',
      'bs': '🇧🇦',
      'bg': '🇧🇬',
      'ca': '🏴',
      'ceb': '🇵🇭',
      'ny': '🇲🇼',
      'zh': '🇨🇳',
      'zh-TW': '🇹🇼',
      'co': '🇫🇷',
      'hr': '🇭🇷',
      'cs': '🇨🇿',
      'da': '🇩🇰',
      'nl': '🇳🇱',
      'en': '🇺🇸',
      'eo': '🌍',
      'et': '🇪🇪',
      'tl': '🇵🇭',
      'fi': '🇫🇮',
      'fr': '🇫🇷',
      'fy': '🇳🇱',
      'gl': '🏴',
      'ka': '🇬🇪',
      'de': '🇩🇪',
      'el': '🇬🇷',
      'gu': '🇮🇳',
      'ht': '🇭🇹',
      'ha': '🇳🇬',
      'haw': '🇺🇸',
      'iw': '🇮🇱',
      'hi': '🇮🇳',
      'hmn': '🇱🇦',
      'hu': '🇭🇺',
      'is': '🇮🇸',
      'ig': '🇳🇬',
      'id': '🇮🇩',
      'ga': '🇮🇪',
      'it': '🇮🇹',
      'ja': '🇯🇵',
      'jw': '🇮🇩',
      'kn': '🇮🇳',
      'kk': '🇰🇿',
      'km': '🇰🇭',
      'ko': '🇰🇷',
      'ku': '🏴',
      'ky': '🇰🇬',
      'lo': '🇱🇦',
      'la': '🏛️',
      'lv': '🇱🇻',
      'lt': '🇱🇹',
      'lb': '🇱🇺',
      'mk': '🇲🇰',
      'mg': '🇲🇬',
      'ms': '🇲🇾',
      'ml': '🇮🇳',
      'mt': '🇲🇹',
      'mi': '🇳🇿',
      'mr': '🇮🇳',
      'mn': '🇲🇳',
      'my': '🇲🇲',
      'ne': '🇳🇵',
      'no': '🇳🇴',
      'ps': '🇦🇫',
      'fa': '🇮🇷',
      'pl': '🇵🇱',
      'pt': '🇵🇹',
      'pa': '🇮🇳',
      'ro': '🇷🇴',
      'ru': '🇷🇺',
      'sm': '🇼🇸',
      'gd': '🏴󠁧󠁢󠁳󠁣󠁴󠁿',
      'sr': '🇷🇸',
      'st': '🇱🇸',
      'sn': '🇿🇼',
      'sd': '🇵🇰',
      'si': '🇱🇰',
      'sk': '🇸🇰',
      'sl': '🇸🇮',
      'so': '🇸🇴',
      'es': '🇨🇴', // Only Spanish with Colombian flag
      'su': '🇮🇩',
      'sw': '🇰🇪',
      'sv': '🇸🇪',
      'tg': '🇹🇯',
      'ta': '🇮🇳',
      'te': '🇮🇳',
      'th': '🇹🇭',
      'tr': '🇹🇷',
      'uk': '🇺🇦',
      'ur': '🇵🇰',
      'uz': '🇺🇿',
      'vi': '🇻🇳',
      'cy': '🏴󠁧󠁢󠁷󠁬󠁳󠁿',
      'xh': '🇿🇦',
      'yi': '🏴',
      'yo': '🇳🇬',
      'zu': '🇿🇦'
    };

    const languages = [];

    if (includeAuto) {
      languages.push({
        code: 'auto',
        name: languageNames['auto'] || t('detectLanguage'),
        flag: flags['auto']
      });
    }

    // Add all other languages and sort them alphabetically by name
    const otherLanguages = [];
    Object.keys(languageNames).forEach(code => {
      if (code !== 'auto') {
        otherLanguages.push({
          code,
          name: languageNames[code],
          flag: flags[code] || '🌐'
        });
      }
    });

    // Sort alphabetically by name in the current interface language
    otherLanguages.sort((a, b) => a.name.localeCompare(b.name, currentLanguage, { sensitivity: 'base' }));

    return [...languages, ...otherLanguages];
  };

  const allLanguages = createLanguagesList();
  const filteredLanguages = allLanguages.filter(lang =>
    lang.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const selectedLanguage = allLanguages.find(lang => lang.code === value);

  return (
    <div className="relative w-full">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-500 dark:hover:border-purple-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent h-[3.5rem]"
      >
        <div className="flex items-center space-x-3 min-w-0 flex-1">
          <span className="text-xl flex-shrink-0">{selectedLanguage?.flag}</span>
          <span className="text-gray-900 dark:text-white font-medium truncate">
            {selectedLanguage?.name}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 ml-2"
        >
          <SafeIcon icon={FiChevronDown} className="w-5 h-5 text-gray-500" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl z-50 max-h-80 overflow-hidden"
          >
            <div className="p-3 border-b border-gray-200 dark:border-gray-700">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('searchLanguages') || 'Buscar idiomas...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto">
              {filteredLanguages.map((language) => (
                <motion.button
                  key={language.code}
                  whileHover={{ backgroundColor: 'rgba(147,51,234,0.1)' }}
                  onClick={() => {
                    onChange(language.code);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 text-left hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors ${
                    value === language.code
                      ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
                      : 'text-gray-900 dark:text-white'
                  }`}
                >
                  <span className="text-xl flex-shrink-0">{language.flag}</span>
                  <span className="font-medium truncate">{language.name}</span>
                </motion.button>
              ))}
              {filteredLanguages.length === 0 && (
                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                  {t('noLanguagesFound') || 'No se encontraron idiomas'}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default TranslationLanguageSelector;